import Amplify, {Hub, MemoryStorage} from "@aws-amplify/core";
import {isLmsHost, redirectToLms} from "../utils/lmsUtils";
import {
  createUrlInterceptor,
  getGuestIdp,
  getRedirectUrl,
  injectTokenManually, isEventAuth
} from "../utils/urlUtils";
import Auth, {CognitoHostedUIIdentityProvider} from "@aws-amplify/auth";
import getEnvConfig from "../utils/config";
import {addPropertiesToPuEnv, getEnvironment} from "../utils/environmentUtils";
import React, {createContext} from "react";
import {
  FederatedSignInOptions
} from "@aws-amplify/auth/lib-esm/types";
import {getTokenRefreshInterval} from "../utils/gameConfigUtils";
import {compress} from "../utils/compressUtils";
import {Base64} from 'js-base64';

export enum AuthEventType {
  RESTORE_STATE = "RestoreState",
  SIGN_OUT = "SignOut",
  UNAUTHORIZED = "Unauthorized",
  TOKEN_REFRESH_ERROR = "TokenRefreshError",
  AUTH_SERVER_NOT_AVAILABLE = "AuthServerNotAvailable"
}

export type AuthEventListener = (eventType: AuthEventType, data?: string) => void;

const MAX_TOKEN_SIZE_IN_BYTES = 8500;

export interface AuthStrategy {

  addListener(listener: AuthEventListener): void;

  removeListener(listener: AuthEventListener): void;

  checkUserLoggedIn(): Promise<void>;

  setOrUpdateEnvironment(): Promise<void>;

  redirectToLogin(customState?: string): void;

  logout(): Promise<void>
}

export abstract class BaseAuthStrategy implements AuthStrategy {

  private listeners: AuthEventListener[] = [];

  constructor(listener?: AuthEventListener) {
    if (listener) {
      this.addListener(listener);
    }
  }

  checkUserLoggedIn(): Promise<void> {
    return Promise.resolve();
  }

  setOrUpdateEnvironment(): Promise<void> {
    return Promise.resolve();
  }

  redirectToLogin(): void {
    throw new Error("Operation not supported.");
  }

  logout(): Promise<void> {
    this.notifyListeners(AuthEventType.SIGN_OUT);
    return Promise.resolve();
  }

  addListener(listener: AuthEventListener): void {
    this.listeners.push(listener);
  }

  removeListener(listener: AuthEventListener): void {
    this.listeners.splice(this.listeners.indexOf(listener), 1)
  }

  notifyListeners(eventType: AuthEventType, data?: string): void {
    this.listeners.forEach(listener => listener(eventType, data));
  }

}

let AuthenticationContext: React.Context<AuthStrategy>;

export const createAuthenticationContext = (listener?: AuthEventListener): void => {
  let authStrategy: AuthStrategy;
  if (injectTokenManually()) {
    authStrategy = new InjectTokenManuallyAuthStrategy(listener);
  } else if (getGuestIdp()) {
    authStrategy = new GuestAuthStrategy(listener);
  } else {
    authStrategy = new GandalfAuthStrategy(listener);
  }

  window.getEnvironment = getEnvironment
  window.updateEnvironmentAsync = async (callback: () => void): Promise<void> => {
    await authStrategy.setOrUpdateEnvironment();
    callback();
  };
  window.logout = (): void => {
    authStrategy.logout();
  }

  AuthenticationContext = createContext(authStrategy);
}

export const getAuthenticationContext = () => {
  return AuthenticationContext;
}

export const getIdToken = (): string => {
  const token = getEnvironment().id_token;
  if (!token) {
    throw new Error("Token not registered yet");
  }
  return token;
}

/**
 * Default authentication strategy. Uses Gandalf to authenticate users.
 */
class GandalfAuthStrategy extends BaseAuthStrategy {

  private refreshTokenInterval: any;

  private threshold = 300; // seconds
  private clockDrift = 0;

  constructor(listener?: AuthEventListener) {
    super(listener);
    this.configureBfCacheListener();
    this.configureAmplifyAuth();
    Hub.listen("auth", ({payload: {event, data}}) => {
      console.log("Auth event: " + event);
      switch (event) {
        case "signOut":
          this.notifyListeners(AuthEventType.SIGN_OUT);
          break;
        case "customOAuthState":
          const path = this.decodeOAuthState(data);
          this.notifyListeners(AuthEventType.RESTORE_STATE, path);
          break;
        case "tokenRefresh":
          this.createRefreshTokenInterval();
          this.refreshSessionIfClockDriftChanged();
          break;
        case "signIn_failure":
        case "customState_failure":
        case "cognitoHostedUI_failure":
          this.notifyListeners(AuthEventType.UNAUTHORIZED);
          break;
        case "tokenRefresh_failure":
          this.notifyListeners(AuthEventType.TOKEN_REFRESH_ERROR);
      }
    });
  }

  async checkUserLoggedIn(): Promise<void> {
    try {
      await Auth.currentAuthenticatedUser();
      await this.setOrUpdateEnvironment();
      this.createRefreshTokenInterval();
    } catch (e) {
      console.log('Failed to get authenticated user: ', e)
      throw e;
    }
  }

  async setOrUpdateEnvironment(): Promise<void> {
    await this.forceRefreshOnClockBackwardChanges();
    const currentSession = await Auth.currentSession();
    const cognitoToken = currentSession.getIdToken();
    var token = cognitoToken.getJwtToken();
    const vibe_id = cognitoToken.decodePayload().vibe_user_id;
    const hat_id = cognitoToken.decodePayload().hat_id;
    const public_provider_name = cognitoToken.decodePayload().public_provider_name;

    const env_config = getEnvConfig();
    const webgl_env = window.GameEnvironment.config.webgl_environment_name;
    const game_id = env_config.webgl_game_id;

    const tokenByteLength = new TextEncoder().encode(token).length;
    if (tokenByteLength > MAX_TOKEN_SIZE_IN_BYTES) {
      console.log(
        "Using compressed token, because token is too big, token size: " + tokenByteLength
      );
      const compressedToken = Base64.fromUint8Array(
        new Uint8Array(compress(token))
      );
      token = compressedToken;
    }
    addPropertiesToPuEnv({
      id_token: token,
      vibe_id: vibe_id,
      hat_id: hat_id,
      env: webgl_env,
      game_id: game_id,
      public_provider_name: public_provider_name,
    });
  }

  redirectToLogin(customState?: string) {
    if (isLmsHost()) {
      redirectToLms();
    } else {
      Auth.federatedSignIn(this.getAuthOptions(customState));
    }
  }

  async logout(): Promise<void> {
    await Auth.signOut();
  }

  private async forceRefreshOnClockBackwardChanges(): Promise<void> {
    const timeNow = new Date().getTime();
    const lastTime = window.lastTokenUpdatedTime;

    window.lastTokenUpdatedTime = timeNow;

    if (lastTime && lastTime - timeNow > 1000) {
      console.log("Client time changed. Forcing renew of amplify token");

      const currentUser = await Auth.currentAuthenticatedUser();
      const currentSession = await Auth.currentSession();
      return new Promise((resolve) =>
        currentUser.refreshSession(currentSession.getRefreshToken(), () =>
          resolve()
        )
      );
    }
  }

  private async refreshSessionIfClockDriftChanged(): Promise<void> {
    const session = await Auth.currentSession();
    const iat = session.getIdToken().getIssuedAt();
    const now = new Date().getTime() / 1000;

    const oldClockDrift = this.clockDrift;
    const newClockDrift = now - iat;
    this.clockDrift = newClockDrift;

    const shouldRefreshSession = Math.abs(newClockDrift - oldClockDrift) > this.threshold;

    if (shouldRefreshSession) {
      const user = await Auth.currentAuthenticatedUser();
      return new Promise((resolve) => user.refreshSession(session.getRefreshToken(), () => resolve()));
    }
  }

  private encodeOAuthState(path: string) {
    return JSON.stringify({path});
  }

  private decodeOAuthState(encodedState: string): string {
    return JSON.parse(encodedState)["path"];
  }

  private getAuthOptions(
    customPath?: string
  ): FederatedSignInOptions {
    const path = customPath
      ? customPath
      : window.location.pathname + window.location.search;

    return {
      customState: this.encodeOAuthState(path),
      provider: CognitoHostedUIIdentityProvider.Cognito
    };
  }

  private configureBfCacheListener() {
    // If bfcache is supported by the browser, force the reload if the user is not authenticated

    const checkSessionStatus = async () => {
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        const currentSession = await Auth.currentSession();
        await new Promise<void>((resolve) =>
          currentUser.refreshSession(currentSession.getRefreshToken(), () =>
            resolve()
          )
        );
        await this.setOrUpdateEnvironment();
        this.createRefreshTokenInterval();
      } catch (e) {
        //invalid session, forcing reload
        window.location.reload();
      }
    }

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        checkSessionStatus();
      }
    });
  }

  private configureAmplifyAuth() {
    const auth = this.getAuth();

    // Amplify.Logger.LOG_LEVEL = 'DEBUG';
    Amplify.configure({
      Auth: {
        region: auth.region,
        userPoolId: auth.userpool_id,
        userPoolWebClientId: auth.app_client_id,
        storage: MemoryStorage,
        oauth: {
          domain: auth.domain,
          scope: ["openid"],
          responseType: "code",
          redirectSignIn: getRedirectUrl(),
          redirectSignOut: this.getLogoutRedirectUrl(auth.app_client_id),
          ...(auth.custom_sign_in_domain && {
            urlOpener: createUrlInterceptor(
              auth.domain,
              auth.custom_sign_in_domain
            ),
          }),
        },
      },
    });
  }

  private getLogoutRedirectUrl(userPoolClientId: string): string {
    return `https://login.us-east-1.auth.skillbuilder.aws/logout?client_id=${userPoolClientId}`;
  }

  private createRefreshTokenInterval() {
    if (this.refreshTokenInterval) {
      clearInterval(this.refreshTokenInterval);
      this.refreshTokenInterval = undefined;
    }
    const interval = getTokenRefreshInterval();
    if (interval <= 0) {
      return;
    }
    this.refreshTokenInterval = setInterval(this.setOrUpdateEnvironment, interval);
  };

  private getAuth() {
    if (isEventAuth()) {
      const auth = getEnvConfig().event_auth;
      if (auth) {
        return auth;
      }
    }
    return getEnvConfig().auth;
  }
}


/**
 * Uses a Cognito IDP to authenticate anonymous users.
 */
class GuestAuthStrategy extends BaseAuthStrategy {


  identityId: string | null = null;
  currentToken: string | null = null;
  clockDrift: number = 0;

  async checkUserLoggedIn(): Promise<void> {
    await this.setOrUpdateEnvironment();
  }

  async setOrUpdateEnvironment(): Promise<void> {
    this.currentToken = "invalid_token"
    this.clockDrift = 0

    const token = this.currentToken;
    const vibe_id = this.identityId;
    const hat_id = this.identityId;

    const env_config = getEnvConfig();
    if (env_config.standalone_webgl) {
      addPropertiesToPuEnv({
        id_token: token,
        vibe_id: vibe_id,
        hat_id: hat_id,
      });
      return;
    }

    const webgl_env = window.GameEnvironment.config.webgl_environment_name;
    const game_id = env_config.webgl_game_id;
    console.log("Will add props to pu_env " + token);
    addPropertiesToPuEnv({
      id_token: token,
      vibe_id: vibe_id,
      hat_id: hat_id,
      env: webgl_env,
      game_id: game_id,
    });
  }
}


/**
 * Used by TestBot to authenticate users with a dev token. Can be
 * enabled only on Alpha and Beta.
 */
class InjectTokenManuallyAuthStrategy extends BaseAuthStrategy {

  async checkUserLoggedIn(): Promise<void> {
    await this.waitForProvidedToken();
    await this.setOrUpdateEnvironment();
  }

  async setOrUpdateEnvironment(): Promise<void> {
    const token = window.providedToken;

    const env_config = getEnvConfig();
    const webgl_env = window.GameEnvironment.config.webgl_environment_name;
    const game_id = env_config.webgl_game_id;

    addPropertiesToPuEnv({
      id_token: token,
      env: webgl_env,
      game_id: game_id,
    });
  }

  private async waitForProvidedToken(): Promise<string> {
    const startTime = new Date().getTime();
    while (!window.providedToken) {
      await new Promise(resolve => window.setTimeout(resolve, 1000));
      if (new Date().getTime() - startTime > 60000) {
        throw new Error("Timeout for token injection.");
      }
    }
    return window.providedToken;
  }

}